import type { QueryClient } from '@tanstack/vue-query';
import {
  deleteAccountShipmentRouteSegment,
  patchAccountShipmentRouteSegment,
  postAccountShipmentRouteSegment,
  usePatchAccountShipmentV2,
  usePostAccountShipmentDevices,
  usePostAccountShipmentPublish,
  usePostAccountShipmentStops,
  usePutAccountShipmentStops,
} from '@gpx-spa/queries';

export type ShipmentFormPersistDeps = {
  queryClient: QueryClient;
  patchShipment: (args: {
    accountId: number;
    shipmentId: number;
    payload: {
      name: string;
      carrier: string | null;
      notes: string | null;
      asset_value: number | null;
    };
  }) => Promise<unknown>;
  postStops: (args: {
    accountId: number;
    shipmentId: number;
    payload: { stops: Record<string, unknown>[] };
  }) => Promise<unknown>;
  putStops: (args: {
    accountId: number;
    shipmentId: number;
    payload: { stops: Record<string, unknown>[] };
    skipInvalidation?: boolean;
  }) => Promise<unknown>;
  postDevices: (args: {
    accountId: number;
    shipmentId: number;
    payload: { device_ids: number[] };
  }) => Promise<unknown>;
  publishShipment: (args: {
    accountId: number;
    shipmentId: number;
  }) => Promise<unknown>;
  postRouteSegment: (
    accountId: number,
    shipmentId: number,
    segment: unknown,
  ) => Promise<unknown>;
  patchRouteSegment: (
    accountId: number,
    shipmentId: number,
    routeId: number,
    segment: unknown,
  ) => Promise<unknown>;
  deleteRouteSegment: (
    accountId: number,
    shipmentId: number,
    routeId: number,
  ) => Promise<unknown>;
};

export function createShipmentFormPersistDeps(
  queryClient: QueryClient,
): ShipmentFormPersistDeps {
  const patchShipmentMutation = usePatchAccountShipmentV2();
  const postStopsMutation = usePostAccountShipmentStops();
  const putStopsMutation = usePutAccountShipmentStops();
  const postDevicesMutation = usePostAccountShipmentDevices();
  const publishMutation = usePostAccountShipmentPublish();

  return {
    queryClient,
    patchShipment: patchShipmentMutation.mutateAsync.bind(patchShipmentMutation),
    postStops: postStopsMutation.mutateAsync.bind(postStopsMutation),
    putStops: putStopsMutation.mutateAsync.bind(putStopsMutation),
    postDevices: postDevicesMutation.mutateAsync.bind(postDevicesMutation),
    publishShipment: publishMutation.mutateAsync.bind(publishMutation),
    postRouteSegment: postAccountShipmentRouteSegment,
    patchRouteSegment: patchAccountShipmentRouteSegment,
    deleteRouteSegment: deleteAccountShipmentRouteSegment,
  };
}
